import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PackagingRecordsViewModel } from "../../viewmodels/packaging/PackagingRecordsViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import {
  convertToTwoDecimals,
  formatDateWithTime,
  withRouter,
} from "../../shared/utility";

import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";
import ExpandableDataTable from "../../components/tables/ExpandableDataTable";
import { Paginator } from "primereact/paginator";

import { classNames } from "primereact/utils";
import ActionButton from "../../components/controls/ActionButton";
import Button from "../../components/ui/Button";

import ReportsDropdown from "../../components/filterDropdown/ReportsDropdown";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import OperationalAnalyticsStrip from "../../components/operational/OperationalAnalyticsStrip";
import PackagingInlineFilters from "../../components/operational/PackagingInlineFilters";
import OperationalTableToolbar from "../../components/operational/OperationalTableToolbar";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import LocationZoneStacked from "../../components/packaging/LocationZoneStacked";
import {
  dedupeSelectedPackagingRows,
  filterSelectableRows,
  getPackagingGroupKey,
  isRowSelectable,
} from "../../shared/packagingBulkActions";
import {
  excludeAllOption,
  formatDateRangeSummary,
  isMultiFilterActive,
} from "../../shared/filterUtils";

const SEARCH_DEBOUNCE_MS = 400;

function isPalletPackagingRow(row) {
  return String(row?.carton_type ?? "").toLowerCase() === "pallet";
}

function PackagingRecords(props) {
  const { logout, addSnackbar } = props;
  const {
    askDeletePackaging,
    emptyModal,
    setEmptyModelOpen,
    getPackagings,
    loading,
    packagings,
    setSlideOver,
    slideOver,
    packagingReprintAll,
    bulkActionInProgress,
    reprintSinglePackagingLabel,
    askDeletePackagingLot,
    openTransferPackagingModal,
    askDeleteSelectedPackaging,
    openPackagingDetailsSideView,
    fetchBrandsForDropdown,
    getUsersForDropdown,
    filters,
    showFilters,
    resetFilters,
    setShowFilters,
    generatePackagingReport,
    reportComponents,
    showReports,
    setShowReports,
    reportInProgress,
    analytics,
    getPackagingSubItems,
    fetchPalletExpand,
    packagingSubItemsByKey,
    subItemsLoadingByKey,
    palletExpandChildren,
    palletExpandLoading,
    fetchProductsForDropdown,
    getProductTypes,
    setShowDates,
    showDates,
    setFilters,
    brands,
    productsForDropdown,
    productTypeOptions,
    supervisorOptions,
    employeeOptions,
  } = PackagingRecordsViewModel(logout, addSnackbar);

  const productOptions = excludeAllOption(productsForDropdown);
  const categoryOptions = excludeAllOption(productTypeOptions);
  const brandOptions = excludeAllOption(brands);

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const searchRef = useRef();
  const searchDebounceRef = useRef(null);

  const getSearchValue = () => searchRef.current?.value?.trim() || null;

  useEffect(() => {
    fetchBrandsForDropdown();
    getUsersForDropdown(2, [1, 2]);
    getUsersForDropdown(1, [1, 2]);
    fetchProductsForDropdown();
    getProductTypes();
    getPackagings(null, 1, filters);
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const [expandedRows, setExpandedRows] = useState({});
  const [nestedExpandedRowsByParent, setNestedExpandedRowsByParent] = useState(
    {},
  );
  const [selectedSubItems, setSelectedSubItems] = useState([]);

  const collapseExpansions = () => {
    setExpandedRows({});
    setNestedExpandedRowsByParent({});
    setSelectedSubItems([]);
  };

  const getExpandedParentRows = () => {
    const data = packagings.data ?? [];
    return data.filter((row) => expandedRows[row.id]);
  };

  const handleSubItemSelectionChange = (items) => {
    setSelectedSubItems(dedupeSelectedPackagingRows(items));
  };

  const getRefreshContext = () => ({
    search: getSearchValue(),
    page: Math.floor(first / rows) + 1,
    filters,
    expandedParents: getExpandedParentRows(),
    expandedPalletBarcodes: Object.values(nestedExpandedRowsByParent).flatMap(
      (rows) =>
        Object.entries(rows ?? {})
          .filter(([, expanded]) => expanded)
          .map(([barcode]) => barcode),
    ),
  });

  const clearSelection = () => setSelectedSubItems([]);

  const selectedCount = filterSelectableRows(selectedSubItems).length;

  const handleSearch = (filterOverride = filters) => {
    collapseExpansions();
    setFirst(0);
    getPackagings(getSearchValue(), 1, filterOverride);
  };

  const scheduleSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      handleSearch();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePagination = (event) => {
    const currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    collapseExpansions();
    getPackagings(getSearchValue(), currentPage, filters);
  };

  const applyFilterChange = (nextFilters) => {
    collapseExpansions();
    setFirst(0);
    setFilters(nextFilters);
    getPackagings(getSearchValue(), 1, nextFilters);
  };

  const applyDateFilter = (startDate, endDate) => {
    const nextFilters = {
      ...filters,
      startDate: startDate !== undefined ? startDate : filters.startDate,
      endDate: endDate !== undefined ? endDate : filters.endDate,
    };
    applyFilterChange(nextFilters);
  };

  const clearDates = () => {
    applyFilterChange({
      ...filters,
      startDate: null,
      endDate: null,
    });
  };

  const handleClearAll = () => {
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    collapseExpansions();
    setShowFilters(false);
    resetFilters();
    setFirst(0);
  };

  const allowGroupedPackagingExpansion = () => true;

  const handleGroupedPackagingExpand = (row) => {
    getPackagingSubItems(row);
  };

  const handlePalletRowExpand = (row) => {
    if (isPalletPackagingRow(row) && row?.barcode) {
      fetchPalletExpand(row.barcode);
    }
  };

  const packagingDateBodyTemplate = (rowData) => {
    return formatDateWithTime(rowData.packaging_converted);
  };

  const packSizeBodyTemplate = (rowData) => {
    return formatDateWithTime(rowData.packing_size) + " kg";
  };

  const totalPackagedBody = (rowData) => {
    return rowData.total_packaged;
  };

  const saleStatusDateBodyTemplate = (rowData) => {
    let bg = "bg-green-600";
    let text = "In Stock";
    if (parseInt(rowData.status) === 1) {
      bg = "bg-red-600";
      text = "Sold";
    } else if (parseInt(rowData.status) === 2) {
      bg = "bg-gray-600";
      text = "Adjusted";
    }
    return (
      <button
        type="button"
        onClick={() => {}}
        className={classNames(
          bg,
          "rounded-md px-1.5 py-1.5 text-sm font-semibold text-white shadow-sm",
        )}
      >
        {text}
      </button>
    );
  };

  const onPackagingReprintAll = async function (itemId, packagingDate) {
    await packagingReprintAll(itemId, packagingDate);
  };

  const bulkBusy =
    bulkActionInProgress.reprintAllLot != null ||
    bulkActionInProgress.barcode !== "" ||
    bulkActionInProgress.transfer ||
    bulkActionInProgress.delete;

  const shippingBarcodeBodyTemplate = (rowData) => {
    return <p>{rowData.barcode}</p>;
  };

  const massItemActionTemplate = (rowData) => {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="soft"
          size="sm"
          onClick={() =>
            openPackagingDetailsSideView(rowData.lot_number, rowData.plu)
          }
          disabled={bulkBusy}
        >
          Details
        </Button>
      </div>
    );
  };

  const singleItemActionTemplate = (rowData) => {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => reprintSinglePackagingLabel(rowData.barcode)}
          disabled={bulkBusy}
          loading={bulkActionInProgress.barcode === rowData.barcode}
        >
          Reprint
        </Button>
        <ActionButton
          color="red"
          onClick={() => {
            askDeletePackaging(rowData);
          }}
          disabled={bulkBusy}
        >
          Delete
        </ActionButton>
      </div>
    );
  };

  const palletSingleCartonReprintActionTemplate = (rowData) => {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => reprintSinglePackagingLabel(rowData.barcode)}
          disabled={bulkBusy}
          loading={bulkActionInProgress.barcode === rowData.barcode}
        >
          Reprint
        </Button>
      </div>
    );
  };

  const stockInWeightBody = (rowData) => {
    return <p>{convertToTwoDecimals(rowData.stock_in_weight)} kg</p>;
  };

  const productNameWithDescriptionTemplate = (rowData) => {
    const description =
      rowData.description_en ?? rowData.description ?? rowData.descriptionEn;

    return (
      <div className="min-w-0">
        <div className="truncate font-semibold text-[#0F172A]">
          {rowData.product_name}
        </div>
        {description ? (
          <div className="line-clamp-2 text-xs leading-4 text-[#64748B]">
            {description}
          </div>
        ) : null}
      </div>
    );
  };

  const locationBodyTemplate = (rowData) => (
    <LocationZoneStacked row={rowData} />
  );

  const subItemColumns = [
    {
      header: "Name",
      body: (r) => productNameWithDescriptionTemplate(r),
      width: "18%",
    },
    { field: "lot_number", header: "Lot Number", width: "10%" },
    { field: "carton_count", header: "Carton Count", width: "8%" },
    {
      header: "Barcode",
      body: (r) => shippingBarcodeBodyTemplate(r),
      width: "16%",
    },
    {
      header: "Location",
      body: (r) => locationBodyTemplate(r),
      width: "12%",
    },
    {
      header: "Pack Size",
      body: (r) => packSizeBodyTemplate(r),
      width: "8%",
    },
    { field: "best_before_date", header: "Best Before", width: "8%" },
    {
      field: "statustatus",
      header: "Sale Status",
      body: (r) => saleStatusDateBodyTemplate(r),
      width: "8%",
    },
    {
      header: "",
      body: (r) => singleItemActionTemplate(r),
      width: "5%",
    },
  ];

  const palletSingleCartonColumns = [
    {
      header: "Barcode",
      body: (r) => shippingBarcodeBodyTemplate(r),
      width: "38%",
    },
    {
      header: "Location",
      body: (r) => locationBodyTemplate(r),
      width: "22%",
    },
    {
      header: "Pack Size",
      body: (r) => packSizeBodyTemplate(r),
      width: "20%",
    },
    {
      header: "",
      body: (r) => palletSingleCartonReprintActionTemplate(r),
      width: "20%",
    },
  ];

  const packagingColumns = [
    { field: "plu", header: "PLU", width: "5%" },
    {
      header: "Name",
      body: (r) => productNameWithDescriptionTemplate(r),
      width: "16%",
    },
    { field: "lot_number", header: "Lot Number", width: "7%" },
    { field: "brand_name", header: "Brand", width: "14%" },
    {
      header: "Location",
      body: (r) => locationBodyTemplate(r),
      width: "11%",
    },
    {
      header: "Packaging Date",
      body: (r) => packagingDateBodyTemplate(r),
      width: "10%",
    },
    {
      field: "best_before_date",
      header: " Best Before Date",
      width: "8%",
    },
    {
      header: "Total Packed/Cs",
      body: (r) => totalPackagedBody(r),
      width: "8%",
    },
    {
      header: "Total Packaged",
      body: (r) => stockInWeightBody(r),
      width: "8%",
    },
    {
      header: "",
      body: (r) => massItemActionTemplate(r),
      width: "6%",
    },
  ];

  const renderPalletSingleCartonsRow = (palletRow) => {
    const bc = palletRow?.barcode;
    const cartons = palletExpandChildren[bc] ?? [];
    const palletLoading = !!palletExpandLoading[bc];
    return (
      <div className="p-3 pl-6">
        <ExpandableDataTable
          data={cartons}
          dataKey="barcode"
          columns={palletSingleCartonColumns}
          loading={palletLoading}
          selectable
          selectionMode="checkbox"
          selectedItems={selectedSubItems}
          onSelectionChange={handleSubItemSelectionChange}
          isRowSelectable={isRowSelectable}
          loadingTemplate={
            <OperationalDataTableSkeleton
              columns={["Barcode", "Location", "Pack Size", "Actions"]}
              rows={3}
            />
          }
          tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
          cellClassName="py-2 bg-gray-50 border-1-bottom-gray px-2 text-sm"
        />
      </div>
    );
  };

  const renderExpandedPackagingRow = (parentRow) => {
    const groupKey = getPackagingGroupKey(parentRow);
    const subItems = packagingSubItemsByKey[groupKey] ?? [];
    const subItemsLoading = !!subItemsLoadingByKey[groupKey];
    const nestedExpandedRows = nestedExpandedRowsByParent[groupKey] ?? {};

    return (
      <div className="p-3">
        <ExpandableDataTable
          data={subItems}
          dataKey="barcode"
          columns={subItemColumns}
          loading={subItemsLoading}
          selectable
          selectionMode="checkbox"
          selectedItems={selectedSubItems}
          onSelectionChange={handleSubItemSelectionChange}
          isRowSelectable={isRowSelectable}
          loadingTemplate={
            <OperationalDataTableSkeleton
              columns={[
                "Name",
                "Lot Number",
                "Carton Count",
                "Barcode",
                "Location",
                "Pack Size",
                "Best Before",
                "Sale Status",
                "Actions",
              ]}
              rows={4}
            />
          }
          expandable
          allowExpansion={isPalletPackagingRow}
          onRowExpand={handlePalletRowExpand}
          renderExpandedRow={renderPalletSingleCartonsRow}
          expandedRows={nestedExpandedRows}
          onExpandedRowsChange={(nextExpandedRows) =>
            setNestedExpandedRowsByParent((prev) => ({
              ...prev,
              [groupKey]: nextExpandedRows,
            }))
          }
          tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
          cellClassName="py-2 bg-gray-50 border-1-bottom-gray px-2 text-sm"
          wrapperClassName="w-full overflow-x-auto"
        />
      </div>
    );
  };

  const totalRecords = packagings?.total ?? 0;
  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );

  const activeFilterCount = [
    isMultiFilterActive(filters.product),
    isMultiFilterActive(filters.category),
    isMultiFilterActive(filters.brand),
    isMultiFilterActive(filters.supervisor),
    isMultiFilterActive(filters.employee1),
  ].filter(Boolean).length;

  const hasAnyFilters = hasDateFilter || activeFilterCount > 0;

  const resultMeta = `${totalRecords.toLocaleString()} result${totalRecords === 1 ? "" : "s"}`;

  const emptyMessage = hasAnyFilters ? (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No packaging records match the current filters.
      </p>
      <Button
        type="button"
        variant="soft"
        size="md"
        className="mt-4"
        onClick={handleClearAll}
      >
        Clear filters
      </Button>
    </div>
  ) : (
    "No packaging records found"
  );

  const tableSkeletonColumns = [
    "PLU",
    "Name",
    "Lot Number",
    "Brand",
    "Location",
    "Packaging Date",
    "Best Before",
    "Total Packed/Cs",
    "Total Packaged",
    "Actions",
  ];

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div className="hide-for-print">
        <div className="space-y-5 px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/packaging-new"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Packaging
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">History</li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <ReportsDropdown
                actionCall={generatePackagingReport}
                reportComponents={reportComponents}
                showReports={showReports}
                setShowReports={setShowReports}
                title="Reports"
                reportInProgress={reportInProgress}
                triggerVariant="secondary"
              />
              <Link
                to="/packaging-new"
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A]"
              >
                <span className="text-lg leading-none">+</span>
                Create packaging
              </Link>
            </div>
          </header>

          <OperationalAnalyticsStrip
            stats={analytics?.stats}
            loading={analytics?.loading}
          />

          <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <OperationalTableToolbar
              searchRow={
                <>
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    name="searchPackaging"
                    id="searchPackaging"
                    className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                    placeholder="Search by packaging name or PLU..."
                    ref={searchRef}
                    onChange={scheduleSearch}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }
                        handleSearch();
                      }
                    }}
                  />
                </>
              }
              filterActions={
                <>
                  <DateRangeFilter
                    onChange={(startDate, endDate) =>
                      setFilters({
                        ...filters,
                        startDate: startDate,
                        endDate: endDate,
                      })
                    }
                    onClear={clearDates}
                    endDate={filters.endDate}
                    startDate={filters.startDate}
                    setShowDates={setShowDates}
                    showDates={showDates}
                    applyChanges={applyDateFilter}
                    maxDate={new Date()}
                    rangeSummary={hasDateFilter ? rangeSummary : undefined}
                  />
                  <Button
                    type="button"
                    variant={showFilters ? "soft" : "secondary"}
                    size="md"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                  >
                    <AdjustmentsHorizontalIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                    {showFilters ? "Hide filters" : "Filters"}
                    {activeFilterCount > 0 ? (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#16A34A] px-1.5 text-xs font-bold leading-none text-white">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </Button>
                </>
              }
              footer={
                showFilters ? (
                  <PackagingInlineFilters
                    filters={filters}
                    onFilterChange={applyFilterChange}
                    productOptions={productOptions}
                    categoryOptions={categoryOptions}
                    brandOptions={brandOptions}
                    supervisorOptions={excludeAllOption(supervisorOptions)}
                    employeeOptions={excludeAllOption(employeeOptions)}
                  />
                ) : null
              }
              trailing={
                <>
                  <span className="text-xs font-medium text-[#64748B]">
                    {resultMeta}
                  </span>
                  {hasAnyFilters ? (
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-xs font-semibold text-[#16A34A] transition hover:text-[#15803D]"
                    >
                      Clear all
                    </button>
                  ) : null}
                </>
              }
            />

            {selectedCount > 0 ? (
              <div className="flex flex-wrap items-center gap-3 border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 sm:px-5">
                <span className="text-sm font-medium text-[#0F172A]">
                  {selectedCount} selected
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs font-semibold text-[#64748B] transition hover:text-[#475569]"
                  disabled={bulkBusy}
                >
                  Clear selection
                </button>
                <div className="ml-auto flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      openTransferPackagingModal(
                        selectedSubItems,
                        getRefreshContext(),
                        clearSelection,
                      )
                    }
                    disabled={bulkBusy}
                    loading={bulkActionInProgress.transfer}
                  >
                    Transfer
                  </Button>
                  <ActionButton
                    color="red"
                    onClick={() =>
                      askDeleteSelectedPackaging(
                        selectedSubItems,
                        getRefreshContext(),
                        clearSelection,
                      )
                    }
                    disabled={bulkBusy}
                  >
                    Delete
                  </ActionButton>
                </div>
              </div>
            ) : null}

            <div className="operational-embedded-table overflow-hidden">
              {loading ? (
                <OperationalDataTableSkeleton
                  columns={tableSkeletonColumns}
                  rows={10}
                />
              ) : (
                <ExpandableDataTable
                  data={packagings.data ?? []}
                  dataKey="id"
                  columns={packagingColumns}
                  loading={false}
                  emptyMessage={emptyMessage}
                  expandable
                  allowExpansion={allowGroupedPackagingExpansion}
                  onRowExpand={handleGroupedPackagingExpand}
                  renderExpandedRow={renderExpandedPackagingRow}
                  expandedRows={expandedRows}
                  onExpandedRowsChange={setExpandedRows}
                  tableClassName="min-w-full bg-white divide-y divide-[#E2E8F0] text-sm"
                  cellClassName="px-4 py-3 text-sm text-[#0F172A] sm:px-5"
                  wrapperClassName="w-full"
                />
              )}
            </div>

            <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
              <Paginator
                className="operational-embedded-paginator"
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        fullScreen={true}
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PackagingRecords),
);
